// Utility function to get evidence by source
export function getEvidenceBySource(source) {
  const state = get(evidenceStore);
  return (state.evidence || []).filter(e => e.source === source);
}

// Utility function to get non-OWID evidence
export function getNonOWIDEvidence() {
  const state = get(evidenceStore);
  return (state.evidence || []).filter(e => e.source !== 'OWID');
}
import { writable, get } from 'svelte/store';
import { apiService } from '../services/apiService.js';
import lunr from 'lunr';
import { API_BASE_URL } from '../config/apiConfig';

const API_URL = API_BASE_URL;

// Initial state with TypeScript-like structure for better clarity
const initialState = {
  evidence: [],              // Search results
  savedEvidence: [],         // Evidence saved to current story
  owidEvidence: [],          // Always-present OWID evidence
  currentStoryId: null,      // Current active story ID
  loadingProgress: {
    total: 0,
    loaded: 0,
    isLoading: false,
    stage: ''
  }
};

// Create store with debug subscription
export const evidenceStore = writable(initialState);

// Flag to prevent repeated fetches
let hasFetchedOWIDEvidence = false;

// Function to normalize OWID evidence schema to match the existing evidence schema
function normalizeOWIDEvidence(owidEvidence) {
  console.log('Normalizing OWID evidence: input length =', Array.isArray(owidEvidence) ? owidEvidence.length : 'not array');
  const normalized = owidEvidence.map(item => ({
    _id: item.chartId || item.id, // Map OWID's `chartId` or `id` to `_id`
    label: item.title || item.name, // Use `title` or `name` as `label`
    description: item.description || item.subtitle || '', // Use `description` or `subtitle`
    mediaURL: item.svgURL || '', // Map `svgURL` to `mediaURL`
    originalURL: item.csvURL || '', // Map `csvURL` to `originalURL`
    sourceURL: item.metadataURL || '', // Map `metadataURL` to `sourceURL`
    sourceLabel: item.source || 'OWID', // Default `sourceLabel` to 'OWID'
    topicTagStatic: item.topic || '', // Map `topic` to `topicTagStatic`
    description_easy: item.description_easy || '', // Map `description_easy` if available
    label_easy: item.label_easy || '', // Map `label_easy` if available
    visual_elements: item.visual_elements || '', // Map `visual_elements` if available
    source: 'OWID', // Add a `source` field to indicate OWID origin
    type: item.type // Preserve the `type` field for OWID-specific logic
  }));
  console.log('Normalized OWID evidence: output length =', normalized.length);
  return normalized;
}

// Function to fetch OWID evidence data from the backend
export const fetchOWIDEvidence = async (options = {}) => {
  console.log('fetchOWIDEvidence function called with options:', options);
  try {
    console.log('Fetching OWID evidence with options:', options);

    const searchParams = new URLSearchParams({
      limit: options.limit || '50', // Default to 50 results
      page: options.page || '1'
    });

    const owidUrl = `${API_BASE_URL}/api/owid?${searchParams.toString()}`;
    console.log(`Making OWID fetch request to: ${owidUrl}`);

    const response = await fetch(owidUrl);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('OWID fetch request failed:', {
        status: response.status,
        statusText: response.statusText,
        error: errorText
      });
      throw new Error(`OWID fetch failed: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const responseData = await response.json();
    console.log('Raw OWID API response:', responseData);

    console.log('Inspecting responseData structure:', responseData);

    if (!responseData || !responseData.data) {
      console.warn('OWID API returned an empty or unexpected response:', responseData);
      // Call normalizeOWIDEvidence with an empty array to confirm its behavior
      normalizeOWIDEvidence([]);
      return {
        results: [],
        pagination: { total: 0, page: 1, pages: 1, limit: 50 },
        hasMore: false
      };
    }

    const normalizedResults = normalizeOWIDEvidence(responseData.data || []); // Normalize OWID data

    console.log('OWID fetch response:', {
      success: responseData.success,
      resultCount: normalizedResults.length,
      pagination: responseData.pagination
    });

    // Update store: append normalized OWID evidence to main evidence array and store separately
    evidenceStore.update(state => {
      // Avoid duplicates: only add OWID evidence not already present
      const existingIds = new Set((state.evidence || []).map(e => e._id));
      const newEvidence = [
        ...(state.evidence || []),
        ...normalizedResults.filter(e => !existingIds.has(e._id))
      ];
      return {
        ...state,
        evidence: newEvidence,
        owidEvidence: normalizedResults,
        loadingProgress: {
          ...state.loadingProgress,
          isLoading: false,
          stage: 'OWID Loaded'
        }
      };
    });

    return {
      results: normalizedResults,
      pagination: responseData.pagination,
      hasMore: responseData.pagination?.hasMore || false
    };
  } catch (error) {
    console.error('Error fetching OWID evidence:', error);

    // Update the store to show error state
    evidenceStore.update(state => ({
      ...state,
      loadingProgress: {
        ...state.loadingProgress,
        isLoading: false,
        stage: 'Error: ' + error.message
      }
    }));

    throw error;
  }
};

// Add debug subscription to track all store changes
// Fetch OWID evidence once on initialization
fetchOWIDEvidence().then(() => {
  console.log('OWID evidence loaded and available in store.');
}).catch(error => {
  console.error('Error loading OWID evidence:', error);
});

// Cache for story store module to avoid repeated imports
let storyStoreModule;
const getStoryStore = async () => {
  if (!storyStoreModule) {
    console.log('Loading story store module...');
    storyStoreModule = await import('./storyStore.js');
    console.log('Story store module loaded');
  }
  return storyStoreModule.storyStore;
};

/**
 * Initialize or update the evidence store with a story's data
 * @param {string} storyId - The ID of the story to load
 * @param {Array} savedEvidence - Array of saved evidence from the story
 */
export const initializeEvidenceStore = async (storyId, savedEvidence = []) => {
  const timestamp = new Date().toISOString();
  console.log('=== Evidence Store Initialization Start ===', {
    timestamp,
    storyId,
    savedEvidenceCount: savedEvidence?.length || 0,
    currentState: get(evidenceStore)
  });
  
  if (!storyId) {
    console.warn('Cannot initialize evidence store: No story ID provided', { timestamp });
    return;
  }

  // Validate and clean saved evidence
  const validEvidence = Array.isArray(savedEvidence) ? 
    savedEvidence.filter(e => e && e._id) : [];

  console.log('Evidence validation results:', {
    originalCount: savedEvidence?.length || 0,
    validCount: validEvidence.length,
    invalidCount: (savedEvidence?.length || 0) - validEvidence.length,
    firstValidId: validEvidence[0]?._id,
    lastValidId: validEvidence[validEvidence.length - 1]?._id
  });

  // Update store with validated evidence
  evidenceStore.update(state => {
    const newState = {
      ...state,
      currentStoryId: storyId,
      savedEvidence: validEvidence
    };
    
    console.log('=== Evidence Store Initialization Complete ===', {
      timestamp: new Date().toISOString(),
      previousState: {
        storyId: state.currentStoryId,
        savedCount: state.savedEvidence?.length || 0
      },
      newState: {
        storyId: newState.currentStoryId,
        savedCount: newState.savedEvidence?.length || 0
      }
    });
    
    return newState;
  });
};

/**
 * Load saved evidence for a specific story
 * @param {string} storyId - The ID of the story to load evidence for
 * @param {Array} savedEvidence - Array of saved evidence items
 */
export const loadSavedEvidenceForStory = (storyId, savedEvidence = []) => {
  const timestamp = new Date().toISOString();
  console.log('=== Loading Saved Evidence Start ===', {
    timestamp,
    storyId,
    evidenceCount: savedEvidence?.length || 0,
    currentStoreState: get(evidenceStore)
  });
  
  if (!storyId) {
    console.warn('Cannot load saved evidence: No story ID provided', { timestamp });
    return;
  }

  // Validate and clean saved evidence
  const validEvidence = Array.isArray(savedEvidence) ? 
    savedEvidence.filter(e => e && e._id) : [];

  console.log('Evidence validation details:', {
    timestamp,
    totalProvided: savedEvidence?.length || 0,
    validItems: validEvidence.length,
    invalidItems: (savedEvidence?.length || 0) - validEvidence.length,
    sampleValidIds: validEvidence.slice(0, 3).map(e => e._id),
    evidenceTypes: validEvidence.slice(0, 3).map(e => typeof e)
  });

  // Update the store with validated evidence
  evidenceStore.update(store => {
    const newState = {
      ...store,
      currentStoryId: storyId,
      savedEvidence: validEvidence
    };
    
    console.log('=== Loading Saved Evidence Complete ===', {
      timestamp: new Date().toISOString(),
      previousState: {
        storyId: store.currentStoryId,
        savedCount: store.savedEvidence?.length || 0,
        hasStoryId: !!store.currentStoryId
      },
      newState: {
        storyId: newState.currentStoryId,
        savedCount: newState.savedEvidence?.length || 0,
        hasStoryId: !!newState.currentStoryId
      },
      storeSnapshot: {
        ...newState,
        savedEvidence: newState.savedEvidence.map(e => ({
          id: e._id,
          type: typeof e,
          hasRequiredFields: !!(e.label && e.description)
        }))
      }
    });
    
    return newState;
  });
};

/**
 * Save an evidence item to the current story
 * @param {Object} evidence - The evidence item to save
 * @returns {Promise<boolean>} - Whether the save was successful
 */
export const saveEvidence = async (evidence) => {
  const timestamp = new Date().toISOString();
  try {
    console.log('=== Save Evidence Start ===', {
      timestamp,
      evidenceId: evidence?._id,
      currentState: get(evidenceStore)
    });

    // Validate evidence object
    if (!evidence?._id) {
      console.error('Cannot save invalid evidence:', { evidence, timestamp });
      return false;
    }

    // Get current state
    const currentState = get(evidenceStore);
    
    // Get story store to check story ID
    const storyStore = await getStoryStore();
    const currentStory = get(storyStore);
    
    // Use either store's story ID or current story's ID
    const storyId = currentState.currentStoryId || currentStory?._id;
    
    if (!storyId) {
      console.error('Cannot save evidence: No active story', {
        timestamp,
        evidenceStoreState: currentState,
        storyStoreState: currentStory
      });
      return false;
    }

    // Check for duplicate
    const isDuplicate = currentState.savedEvidence.some(e => e._id === evidence._id);
    if (isDuplicate) {
      console.log('Evidence already saved:', {
        timestamp,
        evidenceId: evidence._id
      });
      return true;
    }

    // Update evidence store first
    const updatedEvidence = [...(currentState.savedEvidence || []), evidence];
    evidenceStore.update(state => ({
      ...state,
      currentStoryId: storyId,
      savedEvidence: updatedEvidence
    }));

    // Then sync with story store
    try {
      await storyStore.saveStory({
        ...currentStory,
        savedEvidence: updatedEvidence,
        isDirty: true
      });

      console.log('=== Save Evidence Complete ===', {
        timestamp: new Date().toISOString(),
        evidenceId: evidence._id,
        totalSaved: updatedEvidence.length,
        storyId
      });
      
      return true;
    } catch (syncError) {
      console.error('Failed to sync with story:', {
        timestamp,
        error: syncError,
        evidenceId: evidence._id,
        storyId
      });
      // Don't revert evidence store state - let it stay saved locally
      return true;
    }
  } catch (error) {
    console.error('Error saving evidence:', {
      timestamp,
      error,
      evidence: evidence?._id
    });
    return false;
  }
};

/**
 * Remove a saved evidence item from the current story
 * @param {string} evidenceId - The ID of the evidence to remove
 * @returns {Promise<boolean>} - Whether the removal was successful
 */
export const removeSavedEvidence = async (evidenceId) => {
  try {
    if (!evidenceId) {
      console.error('Cannot remove evidence: No ID provided');
      return false;
    }

    // Get current state
    const currentState = get(evidenceStore);
    
    // Validate story context
    if (!currentState.currentStoryId) {
      console.error('Cannot remove evidence: No active story');
      return false;
    }

    // Check if evidence exists
    const evidenceExists = currentState.savedEvidence.some(e => e._id === evidenceId);
    if (!evidenceExists) {
      console.log('Evidence not found:', evidenceId);
      return false;
    }

    // Update evidence store first
    const updatedEvidence = currentState.savedEvidence.filter(e => e._id !== evidenceId);
    evidenceStore.update(state => ({
      ...state,
      savedEvidence: updatedEvidence
    }));

    // Then sync with story store
    try {
      const storyStore = await getStoryStore();
      const currentStory = get(storyStore);

      await storyStore.saveStory({
        ...currentStory,
        savedEvidence: updatedEvidence,
        isDirty: true
      });

      console.log('Successfully removed and synced evidence:', {
        evidenceId,
        remainingSaved: updatedEvidence.length
      });
      return true;
    } catch (syncError) {
      console.error('Failed to sync with story:', syncError);
      // Don't revert evidence store state - let it stay removed locally
      return true;
    }
  } catch (error) {
    console.error('Error removing evidence:', error);
    return false;
  }
};

/**
 * Search for evidence using the provided query
 * @param {string} query - The search query
 * @param {Object} options - Search options
 * @returns {Promise<Array>} - Array of matching evidence items
 */
export const searchEvidence = async (query, options = {}) => {
  try {
    console.log('Search request:', { query, options });

    // Accept bigQuestion from options, fallback to store if not provided
    let bigQuestion = options.bigQuestion;
    if (typeof bigQuestion === 'undefined') {
      const storyStore = await import('./storyStore.js');
      const currentStory = get(storyStore.storyStore);
      bigQuestion = currentStory?.bigQuestion || '';
    }
    console.log('Using bigQuestion for search:', bigQuestion);

    evidenceStore.update(state => ({
      ...state,
      loadingProgress: {
        ...state.loadingProgress,
        isLoading: true,
        stage: 'Searching'
      }
    }));

    // Include both query and bigQuestion in the search request
    const searchParams = new URLSearchParams({
      query: query || '',
      bigQuestion: bigQuestion || '',
      limit: options.limit || '50', // Default to 50 results
      page: options.page || '1'
    });

    const searchUrl = `${API_BASE_URL}/api/evidence/search?${searchParams.toString()}`;
    console.log(`Making search request to: ${searchUrl}`);

    const response = await fetch(searchUrl);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Search request failed:', {
        status: response.status, 
        statusText: response.statusText,
        error: errorText
      });
      throw new Error(`Search failed: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const responseData = await response.json();
    const results = responseData.data || [];

    console.log('Search response:', {
      success: responseData.success,
      query: responseData.query,
      resultCount: results.length,
      pagination: responseData.pagination
    });

    // If the same results keep appearing regardless of query, log this as a warning
    const resultIds = results.map(item => item._id).slice(0, 5).join(',');
    console.log(`First 5 result IDs: ${resultIds}`);

    // Update store with results and pagination info
    evidenceStore.update(state => {
      // If this is page 1, replace results; otherwise append
      const newEvidence = options.page && options.page > 1 
        ? [...state.evidence, ...results]
        : results;

      return {
        ...state,
        evidence: newEvidence,
        pagination: responseData.pagination || {
          total: results.length,
          page: 1,
          pages: 1,
          limit: 50
        },
        loadingProgress: {
          total: responseData.pagination?.total || results.length,
          loaded: newEvidence.length,
          isLoading: false,
          stage: 'Complete'
        }
      };
    });

    return {
      results,
      pagination: responseData.pagination,
      hasMore: responseData.pagination?.hasMore || false
    };
  } catch (error) {
    console.error('Search error:', error);

    // Update the store to show error state
    evidenceStore.update(state => ({
      ...state,
      loadingProgress: {
        ...state.loadingProgress,
        isLoading: false,
        stage: 'Error: ' + error.message
      }
    }));

    throw error;
  }
};

/**
 * Clear all saved evidence (used when creating new story or resetting)
 */
export const clearSavedEvidence = () => {
  console.log('Clearing saved evidence');
  evidenceStore.update(store => ({
    ...store,
    savedEvidence: [],
    currentStoryId: null
  }));
};

// Function to build search index from evidence
function buildSearchIndex(evidence) {
  return lunr(function() {
    this.ref('_id');
    this.field('description');
    this.field('label');
    this.field('text');
    
    // Add documents to index
    evidence.forEach(doc => {
      this.add({
        _id: doc._id,
        description: doc.description || '',
        label: doc.label || '',
        text: doc.text || ''
      });
    });
  });
}

export const fetchSelectedEvidence = async () => {
    console.log('Fetching selected evidence');
};

// Function to select evidence items
export const selectEvidence = (evidenceId) => {
  evidenceStore.update(store => {
    if (!store.selectedEvidenceIds.includes(evidenceId)) {
      store.selectedEvidenceIds.push(evidenceId); // Add the evidence ID if not already selected
    }
    return store;
  });
};

// Function to deselect evidence items
export const deselectEvidence = (evidenceId) => {
  evidenceStore.update(store => {
    store.selectedEvidenceIds = store.selectedEvidenceIds.filter(id => id !== evidenceId); // Remove the evidence ID
    return store;
  });
};

// Function to set current evidence
export const setCurrentEvidence = (evidence) => {
  evidenceStore.update(store => ({
    ...store,
    currentEvidence: evidence
  }));
};

// Function to clear current evidence
export const clearCurrentEvidence = () => {
  evidenceStore.update(store => ({
    ...store,
    currentEvidence: null
  }));
};

// Add a function to load more results
export const loadMoreEvidence = async () => {
  try {
    const state = get(evidenceStore);
    const pagination = state.pagination;
    
    if (!pagination || pagination.page >= pagination.pages) {
      console.log('No more results to load');
      return false;
    }
    
    const nextPage = pagination.page + 1;
    console.log(`Loading more results (page ${nextPage} of ${pagination.pages})`);
    
    const result = await searchEvidence(state.lastQuery, {
      page: nextPage,
      limit: pagination.limit
    });
    
    return result.hasMore;
  } catch (error) {
      console.error('Error loading more evidence:', error);
    }
  }